/**
 * useApi Hook
 *
 * Generic hook for API calls with loading and error state management.
 */

import { useState, useCallback } from 'react';
import { isApiError, getErrorMessage } from '../services/apiClient';

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

interface UseApiReturn<T, Args extends unknown[]> extends UseApiState<T> {
  execute: (...args: Args) => Promise<T | null>;
  reset: () => void;
  setData: (data: T | null) => void;
}

/**
 * Hook for managing API call state
 *
 * @example
 * const { data, isLoading, error, execute } = useApi(clientsService.list);
 *
 * useEffect(() => {
 *   execute({ page: 1 });
 * }, [execute]);
 */
export function useApi<T, Args extends unknown[]>(
  apiFunction: (...args: Args) => Promise<T>
): UseApiReturn<T, Args> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: Args): Promise<T | null> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await apiFunction(...args);
        setState({ data: result, isLoading: false, error: null });
        return result;
      } catch (error) {
        const message = getErrorMessage(error);
        setState((prev) => ({ ...prev, isLoading: false, error: message }));
        return null;
      }
    },
    [apiFunction]
  );

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  return {
    ...state,
    execute,
    reset,
    setData,
  };
}

/**
 * Hook for paginated API calls
 */
interface UsePaginatedApiState<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  error: string | null;
}

interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export function usePaginatedApi<T, Args extends unknown[]>(
  apiFunction: (...args: Args) => Promise<PaginatedResult<T>>
) {
  const [state, setState] = useState<UsePaginatedApiState<T>>({
    data: [],
    pagination: null,
    isLoading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: Args) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await apiFunction(...args);
        setState({
          data: result.data,
          pagination: {
            page: result.pagination.page,
            pageSize: result.pagination.page_size,
            totalItems: result.pagination.total_items,
            totalPages: result.pagination.total_pages,
            hasNext: result.pagination.has_next,
            hasPrev: result.pagination.has_prev,
          },
          isLoading: false,
          error: null,
        });
        return result;
      } catch (error) {
        const message = getErrorMessage(error);
        setState((prev) => ({ ...prev, isLoading: false, error: message }));
        return null;
      }
    },
    [apiFunction]
  );

  const reset = useCallback(() => {
    setState({ data: [], pagination: null, isLoading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * Hook for mutation operations (create, update, delete)
 */
export function useMutation<T, Args extends unknown[]>(
  mutationFunction: (...args: Args) => Promise<T>,
  options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
  }
) {
  const [state, setState] = useState<{
    isLoading: boolean;
    error: string | null;
    isSuccess: boolean;
  }>({
    isLoading: false,
    error: null,
    isSuccess: false,
  });

  const mutate = useCallback(
    async (...args: Args): Promise<T | null> => {
      setState({ isLoading: true, error: null, isSuccess: false });

      try {
        const result = await mutationFunction(...args);
        setState({ isLoading: false, error: null, isSuccess: true });
        options?.onSuccess?.(result);
        return result;
      } catch (error) {
        const message = getErrorMessage(error);
        setState({ isLoading: false, error: message, isSuccess: false });
        options?.onError?.(error as Error);
        return null;
      }
    },
    [mutationFunction, options]
  );

  const reset = useCallback(() => {
    setState({ isLoading: false, error: null, isSuccess: false });
  }, []);

  return {
    ...state,
    mutate,
    reset,
  };
}

export default useApi;
